import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, Check, Globe, MapPin } from 'lucide-react';
import api from '../utils/api';
import { isAuthenticated } from '../utils/auth';

const CITIES = [
  'Bengaluru', 'Mumbai', 'Delhi', 'Hyderabad', 'Chennai',
  'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Gurgaon',
  'Noida', 'Chandigarh', 'Indore', 'Lucknow', 'Kochi',
  'Coimbatore', 'Nagpur', 'Surat', 'Bhopal', 'Other'
];

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Canada', 'Australia',
  'Germany', 'France', 'UAE', 'Singapore', 'Other'
];

const GENDERS = ['Man', 'Woman', 'Non-binary'];
const GENDER_PREFS = ['Men', 'Women', 'Everyone'];

const RELATIONSHIP_TYPES = [
  { id: 'long-term', label: 'Long-term relationship', emoji: '💍' },
  { id: 'casual', label: 'Casual dating', emoji: '🌸' },
  { id: 'friendship', label: 'New friends', emoji: '👋' },
  { id: 'hookup', label: 'Hookup', emoji: '🔥' },
  { id: 'not-sure', label: 'Not sure yet', emoji: '🤷' },
  { id: 'marriage', label: 'Marriage', emoji: '💒' }
];

const INTERESTS = [
  'Coding/Tech', 'Movies/Series', 'Travel', 'Music', 'Fitness',
  'Books', 'Gaming', 'Cooking', 'Art', 'Sports',
  'Startups', 'Photography', 'Dance', 'Fashion', 'Food',
  'Hiking', 'Yoga', 'Meditation', 'Writing', 'Podcasts',
  'Anime', 'Cricket', 'Football', 'Pets', 'Volunteering',
  'Nightlife', 'Wine/Beer', 'Coffee', 'Netflix', 'Music Festivals',
  'Board Games', 'Running', 'Swimming', 'Cycling', 'Camping'
];

const INCOME_RANGES = [
  'Prefer not to say',
  'Below 5 LPA',
  '5-10 LPA',
  '10-20 LPA',
  '20-50 LPA',
  '50+ LPA'
];

const HEIGHT_OPTIONS = [
  "Below 5'0\" (152cm)",
  "5'0\" - 5'3\" (152-160cm)",
  "5'4\" - 5'6\" (163-168cm)",
  "5'7\" - 5'9\" (170-175cm)",
  "5'10\" - 6'0\" (178-183cm)",
  "6'1\" - 6'3\" (185-190cm)",
  "Above 6'3\" (191cm+)"
];

const GYM_OPTIONS = [
  'Regular gym-goer',
  'Occasionally active',
  'Prefer outdoor activities',
  'Not into fitness'
];

const MUSIC_GENRES = [
  'Bollywood', 'Pop', 'Rock', 'Hip-Hop', 'EDM', 'Classical',
  'Jazz', 'R&B', 'Indie', 'Metal', 'Country', 'Sufi', 'Devotional'
];

const IDEOLOGIES = [
  'Liberal', 'Conservative', 'Moderate', 'Apolitical', 'Progressive'
];

const GREEN_FLAGS = [
  'Good listener', 'Emotionally available', 'Ambitious', 'Funny',
  'Family-oriented', 'Adventurous', 'Loyal', 'Honest', 'Supportive'
];

const RED_FLAGS = [
  'Ghosting', 'Jealousy', 'Dishonesty', 'Poor communication',
  'No ambition', 'Disrespectful', 'Controlling', 'Negativity'
];

const LOCATION_PREFERENCES = [
  { id: 'same-city', label: 'Same city only', icon: MapPin, desc: 'Match with people in your city' },
  { id: 'same-country', label: 'Anywhere in my country', icon: MapPin, desc: 'Expand to nationwide matches' },
  { id: 'worldwide', label: 'Worldwide', icon: Globe, desc: 'Connect with people globally' }
];

export default function ProfileSetup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    city: '',
    country: 'India',
    min_age: 18,
    max_age: 35,
    gender: '',
    gender_pref: '',
    interests: [],
    primary_interests: [],
    relationship_type: '',
    bio: '',
    location_preference: 'same-city',
    music_preferences: [],
    ideology: '',
    green_flags: [],
    red_flags: [],
    hobbies: '',
    income_range: '',
    height: '',
    gym: ''
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
    }
  }, [navigate]);

  const toggleItem = (field, item, maxItems = 10) => {
    const current = formData[field];
    if (current.includes(item)) {
      setFormData({ ...formData, [field]: current.filter(i => i !== item) });
    } else if (current.length < maxItems) {
      setFormData({ ...formData, [field]: [...current, item] });
    }
  };

  const togglePrimaryInterest = (interest) => {
    const current = formData.primary_interests;
    if (current.includes(interest)) {
      setFormData({ ...formData, primary_interests: current.filter(i => i !== interest) });
    } else if (current.length < 3) {
      setFormData({ ...formData, primary_interests: [...current, interest] });
    } else {
      toast.error('Maximum 3 primary interests allowed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.city || !formData.gender || !formData.gender_pref) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.interests.length < 3) {
      toast.error('Select at least 3 interests');
      return;
    }

    if (formData.primary_interests.length < 1) {
      toast.error('Select at least 1 primary interest');
      return;
    }

    if (!formData.relationship_type) {
      toast.error('Please select what you\'re looking for');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.put('/user/profile', formData);
      toast.success('Profile created!');
      
      // Women looking for men get free matches - redirect based on verification status
      if (res.data.gets_free_matches) {
        // Check if they need photo verification
        const profileRes = await api.get('/user/profile');
        if (profileRes.data.is_photo_verified) {
          // Verified women go directly to dashboard
          navigate('/dashboard');
        } else {
          // Unverified women need to verify first
          navigate('/verify-photo');
        }
      } else {
        // Men and others need to pay
        navigate('/paywall');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && (!formData.gender || !formData.gender_pref)) {
      toast.error('Please select your gender and preference');
      return;
    }
    if (step === 2 && (!formData.city || !formData.country)) {
      toast.error('Please select your location');
      return;
    }
    if (step === 3 && formData.interests.length < 3) {
      toast.error('Select at least 3 interests');
      return;
    }
    if (step === 3 && formData.primary_interests.length < 1) {
      toast.error('Select at least 1 primary interest (max 3)');
      return;
    }
    setStep(step + 1);
  };

  const totalSteps = 7;

  return (
    <div className="app-container gradient-bg">
      <div className="px-4 py-6 pb-20 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <Heart className="text-primary fill-primary mx-auto mb-3" size={36} />
          <h1 className="text-2xl font-bold text-white text-center mb-2">Set Up Your Profile</h1>
          <p className="text-gray-400 text-sm text-center">Step {step} of {totalSteps}</p>
          {/* Progress Bar */}
          <div className="flex gap-1 mt-4">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < step ? 'bg-primary' : 'bg-white/10'}`} />
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Gender & Preference */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-400 mb-3 block">I identify as</label>
                <div className="grid grid-cols-3 gap-3">
                  {GENDERS.map(gender => (
                    <button
                      key={gender}
                      type="button"
                      onClick={() => setFormData({...formData, gender})}
                      className={`h-14 rounded-xl font-medium transition-all ${
                        formData.gender === gender ? 'bg-primary text-white' : 'bg-secondary/50 text-white border border-white/10'
                      }`}
                    >
                      {gender}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-3 block">I want to meet</label>
                <div className="grid grid-cols-3 gap-3">
                  {GENDER_PREFS.map(pref => (
                    <button
                      key={pref}
                      type="button"
                      onClick={() => setFormData({...formData, gender_pref: pref})}
                      className={`h-14 rounded-xl font-medium transition-all ${
                        formData.gender_pref === pref ? 'bg-primary text-white' : 'bg-secondary/50 text-white border border-white/10'
                      }`}
                    >
                      {pref}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Range Slider */}
              <div>
                <label className="text-sm font-medium text-gray-400 mb-3 block">
                  Preferred Age: <span className="text-primary font-bold">{formData.min_age} - {formData.max_age}</span>
                </label>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Min: {formData.min_age}</span>
                      <span>18 - 100</span>
                    </div>
                    <input
                      type="range"
                      min="18"
                      max="100"
                      value={formData.min_age}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setFormData({
                          ...formData,
                          min_age: val,
                          max_age: Math.max(val, formData.max_age)
                        });
                      }}
                      className="w-full h-2 bg-secondary/50 rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Max: {formData.max_age}</span>
                      <span>18 - 100</span>
                    </div>
                    <input
                      type="range"
                      min="18"
                      max="100"
                      value={formData.max_age}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setFormData({
                          ...formData,
                          max_age: val,
                          min_age: Math.min(val, formData.min_age)
                        });
                      }}
                      className="w-full h-2 bg-secondary/50 rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                  </div>
                </div>
              </div>

              <button type="button" onClick={nextStep} className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full">
                Continue →
              </button>
            </div>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-400 mb-1.5 block">Country</label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white"
                >
                  {COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-1.5 block">City</label>
                <select
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white"
                >
                  <option value="">Select your city</option>
                  {CITIES.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-3 block">Match Location</label>
                <div className="space-y-3">
                  {LOCATION_PREFERENCES.map(pref => (
                    <button
                      key={pref.id}
                      type="button"
                      onClick={() => setFormData({...formData, location_preference: pref.id})}
                      className={`w-full p-4 rounded-xl text-left transition-all flex items-center gap-4 ${
                        formData.location_preference === pref.id 
                          ? 'bg-primary/20 border-2 border-primary' 
                          : 'bg-secondary/50 border border-white/10'
                      }`}
                    >
                      <pref.icon className={formData.location_preference === pref.id ? 'text-primary' : 'text-gray-400'} size={24} />
                      <div>
                        <p className="text-white font-medium">{pref.label}</p>
                        <p className="text-gray-400 text-xs">{pref.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="flex-1 h-12 bg-secondary/50 text-white font-semibold rounded-full border border-white/10">← Back</button>
                <button type="button" onClick={nextStep} className="flex-1 h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full">Continue →</button>
              </div>
            </div>
          )}

          {/* Step 3: Interests */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Primary Interests - Most Important */}
              <div>
                <label className="text-sm font-medium text-gray-400 mb-1.5 block">
                  Primary Interests (1-3) <span className="text-primary">*Most important for matching</span>
                </label>
                <div className="text-sm text-primary mb-3">{formData.primary_interests.length}/3 selected</div>
                <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                  {INTERESTS.map(interest => {
                    const isSelected = formData.primary_interests.includes(interest);
                    const isDisabled = !isSelected && formData.primary_interests.length >= 3;
                    return (
                      <button
                        key={interest}
                        type="button"
                        onClick={() => !isDisabled && togglePrimaryInterest(interest)}
                        disabled={isDisabled}
                        className={`h-11 rounded-xl text-sm transition-all flex items-center justify-center gap-1 ${
                          isSelected ? 'bg-amber-500 text-white ring-2 ring-amber-400' : isDisabled ? 'bg-secondary/30 text-gray-500' : 'bg-secondary/50 text-white border border-white/10'
                        }`}
                      >
                        {isSelected && <Check size={14} />}
                        {interest}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">At least 1 primary interest must match with your potential partner</p>
              </div>

              {/* Secondary Interests */}
              <div>
                <label className="text-sm font-medium text-gray-400 mb-1.5 block">Other interests (3-10)</label>
                <div className="text-sm text-gray-500 mb-3">{formData.interests.length}/10 selected</div>
                <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                  {INTERESTS.filter(i => !formData.primary_interests.includes(i)).map(interest => {
                    const isSelected = formData.interests.includes(interest);
                    const isDisabled = !isSelected && formData.interests.length >= 10;
                    return (
                      <button
                        key={interest}
                        type="button"
                        onClick={() => !isDisabled && toggleItem('interests', interest)}
                        disabled={isDisabled}
                        className={`h-11 rounded-xl text-sm transition-all flex items-center justify-center gap-1 ${
                          isSelected ? 'bg-primary text-white' : isDisabled ? 'bg-secondary/30 text-gray-500' : 'bg-secondary/50 text-white border border-white/10'
                        }`}
                      >
                        {isSelected && <Check size={14} />}
                        {interest}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(2)} className="flex-1 h-12 bg-secondary/50 text-white font-semibold rounded-full border border-white/10">← Back</button>
                <button type="button" onClick={nextStep} className="flex-1 h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full">Continue →</button>
              </div>
            </div>
          )}

          {/* Step 4: Relationship & Music */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-400 mb-3 block">What are you looking for?</label>
                <div className="grid grid-cols-2 gap-3">
                  {RELATIONSHIP_TYPES.map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({...formData, relationship_type: type.id})}
                      className={`p-4 rounded-xl text-left transition-all ${
                        formData.relationship_type === type.id ? 'bg-primary/20 border-2 border-primary' : 'bg-secondary/50 border border-white/10'
                      }`}
                    >
                      <span className="text-2xl block mb-1">{type.emoji}</span>
                      <p className="text-white font-medium text-sm">{type.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-2 block">Music you love (optional)</label>
                <div className="flex flex-wrap gap-2">
                  {MUSIC_GENRES.map(genre => (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => toggleItem('music_preferences', genre, 5)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.music_preferences.includes(genre) ? 'bg-primary text-white' : 'bg-secondary/50 text-white border border-white/10'
                      }`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-2 block">Political views (optional)</label>
                <div className="flex flex-wrap gap-2">
                  {IDEOLOGIES.map(ideology => (
                    <button
                      key={ideology}
                      type="button"
                      onClick={() => setFormData({...formData, ideology})}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.ideology === ideology ? 'bg-primary text-white' : 'bg-secondary/50 text-white border border-white/10'
                      }`}
                    >
                      {ideology}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(3)} className="flex-1 h-12 bg-secondary/50 text-white font-semibold rounded-full border border-white/10">← Back</button>
                <button type="button" onClick={nextStep} className="flex-1 h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full">Continue →</button>
              </div>
            </div>
          )}

          {/* Step 5: Flags & Hobbies */}
          {step === 5 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-400 mb-2 block">Green flags you appreciate</label>
                <div className="flex flex-wrap gap-2">
                  {GREEN_FLAGS.map(flag => (
                    <button
                      key={flag}
                      type="button"
                      onClick={() => toggleItem('green_flags', flag, 5)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.green_flags.includes(flag) ? 'bg-green-600 text-white' : 'bg-secondary/50 text-white border border-white/10'
                      }`}
                    >
                      {flag}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-2 block">Red flags you avoid</label>
                <div className="flex flex-wrap gap-2">
                  {RED_FLAGS.map(flag => (
                    <button
                      key={flag}
                      type="button"
                      onClick={() => toggleItem('red_flags', flag, 5)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.red_flags.includes(flag) ? 'bg-red-600 text-white' : 'bg-secondary/50 text-white border border-white/10'
                      }`}
                    >
                      {flag}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-1.5 block">Other hobbies (optional)</label>
                <textarea
                  value={formData.hobbies}
                  onChange={(e) => setFormData({...formData, hobbies: e.target.value.slice(0, 200)})}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl p-4 text-white placeholder:text-gray-500 resize-none h-20"
                  placeholder="What else do you enjoy doing?"
                  maxLength={200}
                />
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(4)} className="flex-1 h-12 bg-secondary/50 text-white font-semibold rounded-full border border-white/10">← Back</button>
                <button type="button" onClick={nextStep} className="flex-1 h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full">Continue →</button>
              </div>
            </div>
          )}

          {/* Step 6: Additional Details */}
          {step === 6 && (
            <div className="space-y-6">
              <div className="glass rounded-xl p-3 border border-primary/20 mb-4">
                <p className="text-gray-300 text-xs text-center">
                  These details help us find better matches for you (all optional)
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-1.5 block">Height</label>
                <select
                  value={formData.height}
                  onChange={(e) => setFormData({...formData, height: e.target.value})}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white"
                >
                  <option value="">Prefer not to say</option>
                  {HEIGHT_OPTIONS.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-1.5 block">Income Range</label>
                <select
                  value={formData.income_range}
                  onChange={(e) => setFormData({...formData, income_range: e.target.value})}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white"
                >
                  <option value="">Select income range</option>
                  {INCOME_RANGES.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-2 block">Fitness/Gym Habits</label>
                <div className="grid grid-cols-2 gap-2">
                  {GYM_OPTIONS.map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setFormData({...formData, gym: option})}
                      className={`p-3 rounded-xl text-sm transition-all ${
                        formData.gym === option ? 'bg-primary text-white' : 'bg-secondary/50 text-white border border-white/10'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(5)} className="flex-1 h-12 bg-secondary/50 text-white font-semibold rounded-full border border-white/10">← Back</button>
                <button type="button" onClick={nextStep} className="flex-1 h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full">Continue →</button>
              </div>
            </div>
          )}

          {/* Step 7: Bio */}
          {step === 7 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-400 mb-1.5 block">Write a short bio</label>
                <p className="text-xs text-gray-500 mb-3">This helps matches understand you better!</p>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value.slice(0, 300)})}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl p-4 text-white placeholder:text-gray-500 resize-none h-32"
                  placeholder="Tell people about yourself..."
                  maxLength={300}
                />
                <p className="text-xs text-gray-500 mt-1 text-right">{formData.bio.length}/300</p>
              </div>

              {/* Summary */}
              <div className="glass rounded-xl p-4 border border-primary/30">
                <h4 className="text-white font-semibold mb-3">Profile Summary</h4>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-400">{formData.city}, {formData.country}</p>
                  <p className="text-gray-400">{formData.gender} • {formData.min_age}-{formData.max_age} years</p>
                  <p className="text-gray-400">{RELATIONSHIP_TYPES.find(t => t.id === formData.relationship_type)?.label || 'Not specified'}</p>
                  <p className="text-gray-400">{formData.primary_interests.length} primary + {formData.interests.length} interests</p>
                  {formData.height && <p className="text-gray-400">Height: {formData.height}</p>}
                  {formData.gym && <p className="text-gray-400">Fitness: {formData.gym}</p>}
                </div>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(6)} className="flex-1 h-12 bg-secondary/50 text-white font-semibold rounded-full border border-white/10">← Back</button>
                <button type="submit" disabled={loading} className="flex-1 h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full disabled:opacity-50">
                  {loading ? 'Saving...' : 'Complete Profile'}
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="mt-6 text-center">
          <p className="text-green-400 text-sm">🔒 Your info stays anonymous</p>
        </div>
      </div>
    </div>
  );
}
